import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Download,
  Trash2,
  BookOpen,
  Clock,
  Languages,
  ArrowLeft,
  WifiOff,
  Crown,
  FolderOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const OfflineLessonsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [offlineLessons, setOfflineLessons] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isPro = user?.tier === 'pro';

  useEffect(() => {
    loadOfflineLessons();
  }, []);

  const loadOfflineLessons = () => {
    const stored = localStorage.getItem('minnow_offline_lessons');
    if (stored) {
      const lessons = JSON.parse(stored);
      setOfflineLessons(Object.values(lessons));
    }
  };

  const handleDeleteLesson = (lessonId) => {
    const stored = JSON.parse(localStorage.getItem('minnow_offline_lessons') || '{}');
    delete stored[lessonId];
    localStorage.setItem('minnow_offline_lessons', JSON.stringify(stored));
    loadOfflineLessons();
    setDeleteTarget(null);
    toast.success(t('offline.lessonDeleted', 'Lesson removed from offline storage'));
  };

  const handleViewLesson = (lesson) => {
    navigate(`/curriculum/${lesson.moduleId}/lesson/${lesson.lessonId}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(user?.preferred_language || 'en', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getLanguageName = (code) => {
    const languages = {
      en: 'English',
      es: 'Español',
      fr: 'Français',
      pt: 'Português',
      de: 'Deutsch',
      zh: '中文',
      ar: 'العربية',
      hi: 'हिन्दी'
    };
    return languages[code] || code;
  };

  // Non-Pro users see upgrade prompt
  if (!isPro) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-ocean-blue text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="flex items-center h-16">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-white hover:bg-white/10 mr-4"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold text-lg">{t('offline.title', 'Offline Lessons')}</h1>
                <p className="text-xs text-white/70">{t('offline.subtitle', 'Access lessons without internet')}</p>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
          <Card className="border-2 border-dashed border-coral/30 bg-coral/5">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-coral/10 flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-coral" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t('offline.proFeature', 'Pro Feature')}</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                {t('offline.proDescription', 'Download lessons for offline access with Minnow Pro. Learn anywhere, even without internet.')}
              </p>
              <Button 
                onClick={() => navigate('/settings')}
                className="bg-coral hover:bg-coral/90"
              >
                {t('offline.upgradeToPro', 'Upgrade to Pro')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('offline.deleteConfirmTitle', 'Remove Offline Lesson?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('offline.deleteConfirmDesc', 'This will remove the lesson from your offline storage. You can download it again anytime.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeleteLesson(deleteTarget)}
              className="bg-red-500 hover:bg-red-600"
            >
              {t('common.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <header className="bg-ocean-blue text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-white hover:bg-white/10 mr-4"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold text-lg">{t('offline.title', 'Offline Lessons')}</h1>
                <p className="text-xs text-white/70">{offlineLessons.length} {t('offline.lessonsSaved', 'lessons saved')}</p>
              </div>
            </div>
            <Badge variant="secondary" className="bg-white/20 text-white border-0">
              <WifiOff className="w-3 h-3 mr-1" />
              {t('offline.offlineReady', 'Offline Ready')}
            </Badge>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {offlineLessons.length === 0 ? (
          <Card className="border-2 border-dashed">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <FolderOpen className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t('offline.noLessons', 'No Offline Lessons')}</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                {t('offline.noLessonsDesc', 'Download lessons from the curriculum to access them offline. Look for the download button in the lesson menu.')}
              </p>
              <Button onClick={() => navigate('/curriculum')}>
                <BookOpen className="w-4 h-4 mr-2" />
                {t('offline.browseCurriculum', 'Browse Curriculum')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {offlineLessons.map((lesson, index) => (
              <motion.div
                key={lesson.lessonId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">
                          {lesson.lesson?.title || lesson.lessonId}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          {lesson.lesson?.moduleTitle}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline" className="text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            {t('offline.savedOn', 'Saved')} {formatDate(lesson.savedAt)}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            <Languages className="w-3 h-3 mr-1" />
                            {getLanguageName(lesson.language)}
                          </Badge>
                          {lesson.messages && (
                            <Badge variant="outline" className="text-xs">
                              {lesson.messages.length} {t('offline.messages', 'messages')}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteTarget(lesson.lessonId)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleViewLesson(lesson)}
                        >
                          <BookOpen className="w-4 h-4 mr-2" />
                          {t('offline.openLesson', 'Open')}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OfflineLessonsPage;
